import { Account } from '../../../types';

// Most-recently-selected accounts, persisted per user so the account picker can offer a short,
// useful default list on focus instead of fetching the full (thousands-strong) account directory.
export interface RecentAccountsStore {
  storageKey: string;
  maxRecent: number;
}

// Each surface keeps its own list, so picking an account in one place never reorders another's.
export const PM_CONFIG_RECENT_ACCOUNTS: RecentAccountsStore = {
  storageKey: 'pmConfigRecentAccounts',
  maxRecent: 10
};

export const NAV_RECENT_ACCOUNTS: RecentAccountsStore = {
  storageKey: 'navRecentAccounts',
  maxRecent: 4
};

const keyFor = (userId: string | undefined, storageKey: string): string =>
  userId ? `${storageKey}:${userId}` : storageKey;

export const getRecentAccounts = (
  userId?: string,
  store: RecentAccountsStore = PM_CONFIG_RECENT_ACCOUNTS
): Account[] => {
  try {
    const parsed = JSON.parse(localStorage.getItem(keyFor(userId, store.storageKey)) || '[]');
    return Array.isArray(parsed) ? parsed.slice(0, store.maxRecent) : [];
  } catch {
    return [];
  }
};

// Move the account to the front (deduped by id), cap at the store's max, and persist. Returns the new list.
export const pushRecentAccount = (
  account: Account,
  userId?: string,
  store: RecentAccountsStore = PM_CONFIG_RECENT_ACCOUNTS
): Account[] => {
  if (!account?.id) return getRecentAccounts(userId, store);

  const kept = getRecentAccounts(userId, store).filter((a) => a.id !== account.id);
  const next = [account, ...kept].slice(0, store.maxRecent);
  try {
    localStorage.setItem(keyFor(userId, store.storageKey), JSON.stringify(next));
  } catch {
    // Ignore write failures (storage disabled or over quota) — the recent list is a convenience only.
  }
  return next;
};
